import { Box } from "@mui/material";
import { extractErrorMessage, useInvalidateQuery } from "@platform/api";
import {
  type CaseOut,
  deleteCaseMutation,
  importDocumentsMutation,
  startCaseRunMutation,
} from "@platform/api/golden";
import {
  Alert,
  Button,
  ConfirmationModal,
  Link,
} from "@platform/design-system";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router";

import { useGoldenClient } from "../api/GoldenClientProvider";
import { GOLDEN_TAGS } from "../api/goldenTags";

import { useCaseDocuments } from "./caseQueries";

import type { ReactNode } from "react";

export const CASE_ACTIONS_TEST_ID = "golden-case-actions";

/** What a reviewer does to the case as a whole: import its documents, run RUMI on it, or delete it. */
export const CaseActions = ({
  goldenCase,
}: {
  readonly goldenCase: CaseOut;
}): ReactNode => {
  const client = useGoldenClient();
  const invalidateQuery = useInvalidateQuery();
  const navigate = useNavigate();
  const documents = useCaseDocuments(goldenCase.id);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const path = { case_id: goldenCase.id };

  // The import status shares the documents' tag, so the page asks for it again
  // and shows the import running until it finishes.
  const importDocuments = useMutation({
    ...importDocumentsMutation({ client }),
    onSuccess: () => invalidateQuery({ tags: [...GOLDEN_TAGS.documents] }),
  });
  // A started run opens its own page, which follows RUMI as it works.
  const startRun = useMutation({
    ...startCaseRunMutation({ client }),
    onSuccess: async (started) => {
      await invalidateQuery({ tags: [...GOLDEN_TAGS.runs] });
      await navigate(`/cases/${goldenCase.id}/runs/${started.run_id}`);
    },
  });
  const deleteCase = useMutation({
    ...deleteCaseMutation({ client }),
    onSuccess: async () => {
      await invalidateQuery({ tags: [...GOLDEN_TAGS.cases] });
      await navigate("/");
    },
  });

  // The import runs once the baseline is found, and a run needs the broker documents it imports.
  const hasBaseline = goldenCase.state !== "awaiting_refresh";
  const hasBrokerDocuments =
    documents.data?.some((document) => document.kind === "broker") ?? false;
  const isSending =
    importDocuments.isPending || startRun.isPending || deleteCase.isPending;
  const failure = importDocuments.error ?? startRun.error ?? deleteCase.error;

  return (
    <Box
      data-testid={CASE_ACTIONS_TEST_ID}
      sx={{ display: "flex", flexDirection: "column", gap: 2 }}
    >
      {failure && (
        <Alert data-testid={`${CASE_ACTIONS_TEST_ID}-error`} severity="error">
          {extractErrorMessage(failure, "The action failed")}
        </Alert>
      )}
      <Box
        sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}
      >
        <Button
          variant="secondary"
          data-testid={`${CASE_ACTIONS_TEST_ID}-import`}
          disabled={!hasBaseline || isSending}
          loading={importDocuments.isPending}
          onClick={() => importDocuments.mutate({ path })}
        >
          Import documents
        </Button>
        <Button
          data-testid={`${CASE_ACTIONS_TEST_ID}-run`}
          disabled={!hasBrokerDocuments || isSending}
          title={
            hasBrokerDocuments ? undefined : "Import the broker documents first"
          }
          loading={startRun.isPending}
          onClick={() =>
            startRun.mutate({ path, body: { replay_kind: "submission" } })
          }
        >
          Run RUMI
        </Button>
        <Link component={RouterLink} to={`/review?case=${goldenCase.id}`}>
          Open discrepancies
        </Link>
        <Button
          variant="danger"
          data-testid={`${CASE_ACTIONS_TEST_ID}-delete`}
          disabled={isSending}
          onClick={() => setConfirmingDelete(true)}
        >
          Delete case
        </Button>
      </Box>
      <ConfirmationModal
        open={confirmingDelete}
        title={`Delete case #${goldenCase.id}?`}
        description="It leaves every test group; releases that hold it keep it."
        confirmButtonText="Delete case"
        cancelButtonText="Keep it"
        loading={deleteCase.isPending}
        onCancel={() => setConfirmingDelete(false)}
        onAccept={() => {
          setConfirmingDelete(false);
          deleteCase.mutate({ path });
        }}
      />
    </Box>
  );
};
